/* eslint-disable camelcase */
import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { PlayerProfileLink } from 'widgets/statspro/js/components/common/PlayerProfileLink';
import { TeamLineupsContext } from 'widgets/mc-team-lineups/js/components/MCTeamLineups';

const POSITION_ROWS = [
    'backs',
    'halfBacks',
    'centres',
    'halfForwards',
    'forwards',
    'followers',
    'interchanges',
    'substitutes',
    'emergencies'
];

// This needs to match the key in the PlayersModel in TeamAnnouncements.jsx
const SUBS_KEY = 'substitutes';

/**
 * Renders the jumper number and player name.
 *
 * @param {object} props - The props
 * @param {object} props.players - The players to display
 * @param {string} props.teamAbbr - The abbreviation of the team
 * @param {string} props.teamLocation - Home or away
 * @param {string} props.matchStatus - The match status
 * @param {boolean} props.isAFLW - Check if competition is AFLW
 * @param {string} props.clubId - The player's club/team ID (used to determine if player is from different club)
 * @returns {React.JSX.Element}    - The rendered jumper number and player name.
 */
export const TeamAnnouncementsList = ({
    players,
    teamAbbr,
    teamLocation,
    matchStatus,
    isAFLW,
    clubId
}) => {
    const teamLineupsContext = useContext(TeamLineupsContext);

    return POSITION_ROWS.map((item, index) => {
        // Only show subs row for certain match statuses
        if (
            item === SUBS_KEY &&
            !PULSE.app.common.CONSTANTS.DISPLAY_MATCH_STATUSES.includes(
                matchStatus
            )
        ) {
            return null;
        }

        const playerDetails = [];
        const playingPositionGroupTranslation =
            'label.playingposition.group.' + item;

        if (players[item]) {
            for (const player of players[item]) {
                playerDetails.push(
                    <Fragment key={`player_${index}`}>
                        <span className="team-announcements__player-info">
                            <span
                                key={`player_title_${index}`}
                                className={`team-announcements__jumper-number team-lineups__position-meta-label team-lineups__position-meta-label--${teamLocation} ${teamAbbr.toLowerCase()}`}
                            >
                                {player.jumperNumber}
                            </span>
                            <PlayerProfileLink
                                key={player.player.playerId}
                                classes="team-announcements__link"
                                player={{
                                    playerId: player?.player.player?.playerId,
                                    firstName:
                                        player?.player.player?.playerName
                                            ?.givenName,
                                    surname:
                                        player?.player.player?.playerName
                                            ?.surname
                                }}
                                trackingInfo={{
                                    event: PULSE.app.common.CONSTANTS.TRACKING
                                        .EVENTS.SELECT.LINK,
                                    component_name:
                                        PULSE.app.common.CONSTANTS.TRACKING
                                            .VALUES.TEAM_LINEUPS.VIEW.LIST,
                                    component_content_type:
                                        teamLineupsContext === 'match-centre'
                                            ? PULSE.app.common.CONSTANTS
                                                  .TRACKING.CONTENT_TYPES
                                                  .MATCH_CENTRE.LINEUPS
                                            : PULSE.app.common.CONSTANTS
                                                  .TRACKING.CONTENT_TYPES
                                                  .LINEUPS
                                }}
                                isAFLW={isAFLW}
                                clubId={clubId}
                            >
                                <span
                                    key={`player_value_${index}`}
                                    className="team-announcements__player-name"
                                >
                                    {player.player.player.playerName.givenName}{' '}
                                    {player.player.player.playerName.surname}
                                </span>
                            </PlayerProfileLink>
                        </span>
                    </Fragment>
                );
            }

            return (
                <div
                    key={`${item}-${index}`}
                    className="team-announcements__list-title team-announcements__position-group"
                >
                    <div className="team-announcements__list-title">
                        <span className="team-announcements__position-title">
                            {PULSE.I18N.lookup(playingPositionGroupTranslation)}
                        </span>
                    </div>

                    <div className="team-announcements__list-values">
                        {playerDetails.map((position, detailsIndex) => (
                            <Fragment key={`player-position_${detailsIndex}`}>
                                {position}
                            </Fragment>
                        ))}
                    </div>
                </div>
            );
        }

        return null;
    });
};

TeamAnnouncementsList.propTypes = {
    players: PropTypes.object.isRequired,
    teamAbbr: PropTypes.string,
    teamLocation: PropTypes.string.isRequired,
    matchStatus: PropTypes.string,
    isAFLW: PropTypes.bool,
    clubId: PropTypes.string
};
